import Link from 'next/link';
import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { AdTypeEnum } from '@/__generated_graphql__/graphql';
import { getStatus } from '@/components/Card/Card.helpers';
import { CardProps, WrapperProps } from '@/components/Card/Card.types';
import { CardMenu } from '@/components/Card/CardMenu';
import { ProgressBar } from '@/components/Card/ProgressBar';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import {
  BookmarkIcon,
  BumpIcon,
  CrownIcon,
  LightningIcon,
  VerifiedIcon
} from '@/components/ui/Icons/Icons';
import { AD_TYPE, CardStatus } from '@/constants/common';
import { PAYMENT_MODAL_CY } from '@/constants/test-id';
import { useToggleBookmark } from '@/hooks/useToggleBookmark';
import { useStableNow } from '@/providers/ServerNowProvider';
import { useTopProgressBar } from '@/providers/TopProgressBarProvider';

import * as S from './Card.styles';

const { APPROVED, PENDING, AWAITING_PAYMENT } = CardStatus;

const Wrapper: FC<WrapperProps> = ({ href, onClick, children }) => {
  if (href) {
    return (
      <Link href={href} onClick={onClick} prefetch={false}>
        {children}
      </Link>
    );
  }

  return <>{children}</>;
};

export const Card: FC<CardProps> = props => {
  const {
    id,
    title,
    subtitle,
    updatedAt,
    createdAt,
    bumpedAt,
    location,
    salaryFrom,
    salaryTo,
    status,
    image,
    daysLeft,
    activeTab,
    isBookmarked,
    isFeatured = false,
    isBumped = false,
    isDeactivated,
    isVerified = false,
    isMine,
    isProfile,
    __typename,
    className,
    link,
    onPromote,
    onActivate,
    disabled = false
  } = props;

  const { isLoading } = useTopProgressBar();
  const isLocked = disabled || isLoading;

  const showActions =
    isProfile && isMine && !!status && ![PENDING].includes(status);

  const adType =
    __typename === AD_TYPE.RESUME ? AdTypeEnum.Resume : AdTypeEnum.Vacancy;
  const isResume = adType === AdTypeEnum.Resume;

  const dateValue = isProfile ? (createdAt ?? updatedAt) : bumpedAt;

  const { t } = useTranslation();
  const now = useStableNow();
  const toggleBookmark = useToggleBookmark();

  const handleToggleBookmark = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLocked || !id) return;

    toggleBookmark(id, adType, isBookmarked);
  };

  const handleActivate = (
    e: MouseEvent<HTMLLinkElement> | MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (isLocked) return;

    onActivate?.();
  };

  const handlePromote = (
    e: MouseEvent<HTMLLinkElement> | MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (isLocked) return;

    onPromote?.();
  };

  return (
    <Wrapper href={link}>
      <S.CardWrapper
        className={className}
        data-cy="ad-card"
        $isLink={!!link}
        $isDeactivated={isDeactivated}
        $isResume={isResume}
        $isMine={isProfile && isMine}
      >
        <S.CardHeader>
          <S.CompanyInfo $isDeactivated={isDeactivated}>
            <Avatar
              dataCy="ad-card-logo"
              sizes={{ mobile: 35 }}
              name={title}
              image={image}
              showInitials={adType === AdTypeEnum.Vacancy}
            />
            <S.CompanyName data-cy="ad-card-title">{title}</S.CompanyName>
            {isVerified && <VerifiedIcon />}
          </S.CompanyInfo>

          {isProfile && isMine ? (
            <S.Status data-cy="ad-card-status" $status={status}>
              {t(getStatus(status))}
            </S.Status>
          ) : isBookmarked !== undefined ? (
            <S.AddToBookmarksButton
              disabled={isLocked || (isDeactivated && !isBookmarked)}
              data-testid="bookmarkButton"
              aria-label="Add to bookmark"
              $isBookmarked={isBookmarked}
              $isDeactivated={isDeactivated && !isBookmarked}
              onClick={handleToggleBookmark}
            >
              <BookmarkIcon />
            </S.AddToBookmarksButton>
          ) : null}
        </S.CardHeader>

        <S.CardBody>
          <S.JobTitle data-cy="ad-card-subtitle" $isDeactivated={isDeactivated}>
            {subtitle}
          </S.JobTitle>

          <S.Salary $isDeactivated={isDeactivated} data-cy="ad-card-salary">
            {t('Card.salary', {
              val: { salaryFrom, salaryTo },
              interpolation: { escapeValue: false }
            })}
          </S.Salary>
        </S.CardBody>

        {showActions && status === APPROVED && !isBumped && (
          <ProgressBar daysLeft={daysLeft} />
        )}

        <S.CardActions>
          {showActions && (
            <>
              {status === APPROVED && (
                <S.PromoteButton
                  startIcon={<LightningIcon />}
                  onClick={handlePromote}
                  data-cy={PAYMENT_MODAL_CY.OPEN_PROMOTE_BTN}
                  disabled={isLocked}
                >
                  {t('Card.advertise_label')}
                </S.PromoteButton>
              )}
              {status === AWAITING_PAYMENT && (
                <S.PayButton
                  size="s"
                  onClick={handleActivate}
                  disabled={isLocked}
                >
                  {t('Card.complete_label')}
                </S.PayButton>
              )}
              {isDeactivated && (
                <S.ActivateButton
                  data-cy="ad-card-activate"
                  onClick={handleActivate}
                  disabled={isLocked}
                >
                  {t('Card.activate_label')}
                </S.ActivateButton>
              )}
            </>
          )}

          <S.CardFooter>
            <S.LocationDate $isDeactivated={isDeactivated}>
              {location && <span data-cy="ad-card-location">{location}</span>}
              {location && dateValue && <S.Dot />}
              {dateValue && (
                <span data-cy="ad-card-date">
                  {t('Card.intlDateTime', { val: dateValue, now })}
                </span>
              )}
            </S.LocationDate>

            <S.BottomActions>
              <S.ChipsWrapper>
                {isFeatured && (
                  <S.Chip>
                    <CrownIcon />
                  </S.Chip>
                )}
                {isBumped && (
                  <S.Chip>
                    <BumpIcon />
                  </S.Chip>
                )}
              </S.ChipsWrapper>

              {showActions && (
                <CardMenu
                  onPromote={onPromote}
                  subtitle={subtitle}
                  adType={adType}
                  id={id}
                  status={status}
                  activeTab={activeTab}
                  disabled={isLocked}
                />
              )}
            </S.BottomActions>
          </S.CardFooter>
        </S.CardActions>
      </S.CardWrapper>
    </Wrapper>
  );
};
